import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  activeMaintenanceQuery,
  appMaintenanceQuery,
  endMaintenanceNow,
  removeMaintenanceWindow,
  saveMaintenanceWindow,
  type AppMaintenanceCard,
  type MaintenanceRun,
  type MaintenanceWindow,
} from '../api/appMaintenance'
import { Modal } from '../ui/Modal'
import { toast } from '../ui/notify'
import { failed } from './parts'
import './AppMaintenance.css'

const DAYS: { value: number; label: string }[] = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 7, label: 'Sun' },
]

const TITLE = 'Maintenance windows'

/**
 * Insigna "In maintenance" - in lista aplicatiilor, pe fisa si pe harta. O
 * singura cerere pentru toata pagina (aceeasi cheie de interogare), si nimic
 * cand serverul n-are inca tabelele sau omul n-are dreptul.
 */
export function MaintenanceBadge({ appId }: { appId: number }) {
  const active = useQuery({ ...activeMaintenanceQuery(), retry: false })
  const apps = Array.isArray(active.data?.apps) ? active.data.apps : []
  const hit = apps.find((a) => a.appId === appId)
  if (!hit) return null
  return (
    <span className="adm-pill app-maint-pill" title={`${hit.window} - until ${hit.until} (${hit.timezone}): alarms on the components do not open tickets or notify`}>
      <i className="fas fa-tools"></i> In maintenance
    </span>
  )
}

/**
 * "Maintenance windows" pe fisa aplicatiei: ferestrele (o data sau
 * saptamanal), urmatoarea aparitie, cea in curs cu "End now", istoria - cate
 * aparate au fost tacute, cate alarme s-au inregistrat fara tichet, cate
 * tichete au primit nota.
 */
export function AppMaintenance({ appId }: { appId: number }) {
  const card = useQuery({ ...appMaintenanceQuery(appId), retry: false })
  const query = useQueryClient()
  const [editing, setEditing] = useState<MaintenanceWindow | 'new' | null>(null)
  const [showHistory, setShowHistory] = useState(false)
  const data = card.data
  if (!data?.ready || !Array.isArray(data.windows)) return null

  const reload = () => void query.invalidateQueries({ queryKey: ['business-apps', 'maintenance'] })
  const windows = data.windows
  const history = (data.history ?? []).filter((r) => !r.active)

  const remove = (w: MaintenanceWindow) => {
    if (!window.confirm(`Remove the maintenance window "${w.name}"? If it is running now, alarms come back right away.`)) return
    removeMaintenanceWindow(w.id)
      .then((r) => {
        toast(r.message, TITLE, r.status === 'OK' ? 'success' : 'error')
        if (r.status === 'OK') reload()
      })
      .catch((e) => toast(failed(e), TITLE, 'error'))
  }

  const endNow = (run: MaintenanceRun) => {
    if (!window.confirm('End this maintenance now? Alarms on the components open tickets and notify again from this moment.')) return
    endMaintenanceNow(run.id)
      .then((r) => {
        toast(r.message, TITLE, r.status === 'OK' ? 'success' : 'error')
        if (r.status === 'OK') reload()
      })
      .catch((e) => toast(failed(e), TITLE, 'error'))
  }

  return (
    <>
      <div className="adm-section">
        <h4>{TITLE}</h4>
        <span className="adm-section-note">alarms on the components are recorded, but open no tickets and send nothing</span>
        {data.canEdit && (
          <button type="button" id="appMaintAdd" className="btn btn-default btn-xs" style={{ marginLeft: 'auto' }} onClick={() => setEditing('new')}>
            <i className="fa fa-plus"></i> Add window
          </button>
        )}
      </div>
      <div className="adm-card" id="appMaintenance">
        {data.active ? (
          <div className="app-maint-now" id="appMaintActive">
            <i className="fas fa-tools"></i>
            <div>
              <strong>In maintenance now</strong>: {data.active.name}, until {data.active.endsAt} ({data.active.timezone})
              <div className="adm-muted small">
                {data.active.devices} device{data.active.devices === 1 ? '' : 's'} quiet
                {data.active.ticketsNoted > 0 ? ` · ${data.active.ticketsNoted} ticket${data.active.ticketsNoted === 1 ? '' : 's'} noted` : ''}
              </div>
            </div>
            {data.canEdit && (
              <button type="button" id="appMaintEnd" className="btn btn-warning btn-xs" onClick={() => endNow(data.active as MaintenanceRun)}>
                End now
              </button>
            )}
          </div>
        ) : data.next ? (
          <p className="app-maint-next" id="appMaintNext">
            Next: <strong>{data.next.name}</strong>, {data.next.startsAt} - {data.next.endsAt} <span className="adm-muted">({data.next.timezone})</span>
          </p>
        ) : null}

        {windows.length === 0 ? (
          <p className="text-muted" style={{ padding: '10px 15px' }}>
            No maintenance window. Add one - for example the monthly update, every Tuesday 22:00-02:00 - and alarms on the
            components will not wake anyone up during it.
          </p>
        ) : (
          <table className="table adm-list" id="appMaintWindows">
            <tbody>
              {windows.map((w) => (
                <tr key={w.id}>
                  <td>
                    <strong>{w.name}</strong>
                    <div className="adm-muted small">{w.schedule}</div>
                    {w.description !== '' && <div className="small app-maint-desc">{w.description}</div>}
                  </td>
                  <td className="small">
                    {w.announce ? (
                      <span>
                        <i className="far fa-envelope"></i> announced {w.announceHours}h before to {w.announceTo.length} contact
                        {w.announceTo.length === 1 ? '' : 's'}
                      </span>
                    ) : (
                      <span className="adm-muted">not announced</span>
                    )}
                  </td>
                  {data.canEdit && (
                    <td className="text-right adm-nowrap">
                      <button type="button" className="btn btn-link btn-xs" aria-label={`Edit ${w.name}`} onClick={() => setEditing(w)}>
                        <i className="fa fa-pencil-alt"></i>
                      </button>
                      <button type="button" className="btn btn-link btn-xs" aria-label={`Remove ${w.name}`} onClick={() => remove(w)}>
                        <i className="fa fa-trash"></i>
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {history.length > 0 && (
          <div className="app-maint-history">
            <button type="button" id="appMaintHistory" className="btn btn-link btn-xs" onClick={() => setShowHistory(!showHistory)}>
              {showHistory ? 'Hide' : 'Show'} history ({history.length})
            </button>
            {showHistory && (
              <table className="table adm-list">
                <tbody>
                  {history.map((r) => (
                    <HistoryLine key={r.id} run={r} />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
      {editing !== null && (
        <WindowModal
          appId={appId}
          card={data}
          window={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            reload()
          }}
        />
      )}
    </>
  )
}

function HistoryLine({ run }: { run: MaintenanceRun }) {
  const label = run.state === 'cancelled' ? 'Cancelled' : run.state === 'scheduled' ? 'Upcoming' : run.endedEarly ? 'Ended early' : 'Done'
  return (
    <tr>
      <td>
        {run.name}
        <div className="adm-muted small">
          {run.startsAt} - {run.endsAt} ({run.timezone})
        </div>
      </td>
      <td className="small">
        <span className={`adm-pill ${run.state === 'cancelled' ? '' : 'adm-pill-ok'}`}>{label}</span>
      </td>
      <td className="small adm-muted">
        {run.alarmsHushed} alarm{run.alarmsHushed === 1 ? '' : 's'} recorded without ticket
        {run.ticketsNoted > 0 ? ` · ${run.ticketsNoted} ticket${run.ticketsNoted === 1 ? '' : 's'} noted` : ''}
        {run.announced ? ` · announced to ${run.announcedCount}` : ''}
      </td>
    </tr>
  )
}

function WindowModal({
  appId,
  card,
  window: editing,
  onClose,
  onSaved,
}: {
  appId: number
  card: AppMaintenanceCard
  window: MaintenanceWindow | null
  onClose(): void
  onSaved(): void
}) {
  const [name, setName] = useState(editing?.name ?? '')
  const [description, setDescription] = useState(editing?.description ?? '')
  const [kind, setKind] = useState<1 | 2>(editing?.kind ?? 2)
  const [startsAt, setStartsAt] = useState(editing?.startsAt ?? '')
  const [endsAt, setEndsAt] = useState(editing?.endsAt ?? '')
  const [days, setDays] = useState<number[]>(editing?.days ?? [])
  const [startTime, setStartTime] = useState(editing?.startTime || '22:00')
  const [endTime, setEndTime] = useState(editing?.endTime || '02:00')
  const [timezone, setTimezone] = useState(editing?.timezone ?? card.timezone ?? '')
  const [announce, setAnnounce] = useState(editing?.announce ?? false)
  const [hours, setHours] = useState(String(editing?.announceHours ?? 24))
  const [to, setTo] = useState<number[]>(editing?.announceTo ?? [])
  const [busy, setBusy] = useState(false)
  const people = card.people ?? []

  const toggle = (list: number[], value: number) => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value])

  const save = () => {
    setBusy(true)
    saveMaintenanceWindow(appId, {
      ID: String(editing?.id ?? 0),
      name,
      description,
      kind: String(kind),
      starts_at: startsAt.replace('T', ' '),
      ends_at: endsAt.replace('T', ' '),
      days: days.map(String),
      start_time: startTime,
      end_time: endTime,
      timezone,
      announce: announce ? '1' : '0',
      announce_hours: hours,
      announce_to: to.map(String),
    })
      .then((r) => {
        toast(r.message, TITLE, r.status === 'OK' ? 'success' : 'error')
        if (r.status === 'OK') onSaved()
      })
      .catch((e) => toast(failed(e), TITLE, 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <Modal
      id={`appMaint-${appId}`}
      open
      onClose={onClose}
      title={editing ? 'Edit maintenance window' : 'New maintenance window'}
      footer={
        <>
          <button type="button" className="btn btn-default" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" id="appMaintSave" disabled={busy} onClick={save}>
            Save
          </button>
        </>
      }
    >
      <div className="form-group">
        <label htmlFor="amName">Name</label>
        <input id="amName" className="form-control" maxLength={120} placeholder="e.g. Monthly update" value={name} onChange={(ev) => setName(ev.target.value)} />
      </div>
      <div className="form-group">
        <label htmlFor="amDescription">Description</label>
        <textarea id="amDescription" className="form-control" rows={2} value={description} onChange={(ev) => setDescription(ev.target.value)} />
      </div>
      <div className="form-group">
        <label className="radio-inline">
          <input type="radio" name="amKind" id="amKindWeekly" checked={kind === 2} onChange={() => setKind(2)} /> Every week
        </label>
        <label className="radio-inline">
          <input type="radio" name="amKind" id="amKindOnce" checked={kind === 1} onChange={() => setKind(1)} /> Once
        </label>
      </div>
      {kind === 1 ? (
        <div className="app-maint-row">
          <div className="form-group">
            <label htmlFor="amStarts">From</label>
            <input id="amStarts" type="datetime-local" className="form-control" value={startsAt.replace(' ', 'T')} onChange={(ev) => setStartsAt(ev.target.value)} />
          </div>
          <div className="form-group">
            <label htmlFor="amEnds">To</label>
            <input id="amEnds" type="datetime-local" className="form-control" value={endsAt.replace(' ', 'T')} onChange={(ev) => setEndsAt(ev.target.value)} />
          </div>
        </div>
      ) : (
        <>
          <div className="form-group app-maint-days">
            {DAYS.map((d) => (
              <label key={d.value} className="checkbox-inline">
                <input type="checkbox" id={`amDay${d.value}`} checked={days.includes(d.value)} onChange={() => setDays(toggle(days, d.value))} /> {d.label}
              </label>
            ))}
          </div>
          <div className="app-maint-row">
            <div className="form-group">
              <label htmlFor="amFrom">From</label>
              <input id="amFrom" type="time" className="form-control" value={startTime} onChange={(ev) => setStartTime(ev.target.value)} />
            </div>
            <div className="form-group">
              <label htmlFor="amTo">To</label>
              <input id="amTo" type="time" className="form-control" value={endTime} onChange={(ev) => setEndTime(ev.target.value)} />
            </div>
          </div>
          <p className="help-block">An end before the start runs past midnight: Tue 22:00-02:00 ends on Wednesday morning.</p>
        </>
      )}
      <div className="form-group">
        <label htmlFor="amTz">Time zone</label>
        <input id="amTz" className="form-control" value={timezone} placeholder="Europe/Bucharest" onChange={(ev) => setTimezone(ev.target.value)} />
      </div>
      <div className="form-group">
        <label className="checkbox-inline">
          <input type="checkbox" id="amAnnounce" checked={announce} onChange={(ev) => setAnnounce(ev.target.checked)} /> Announce to the customer by email
        </label>
      </div>
      {announce && (
        <>
          <div className="form-group app-maint-hours">
            <label htmlFor="amHours">Hours before</label>
            <input id="amHours" type="number" min={1} max={168} className="form-control" value={hours} onChange={(ev) => setHours(ev.target.value)} />
          </div>
          <div className="form-group">
            <label>Contacts</label>
            {people.length === 0 ? (
              <p className="help-block">This customer has no contact with an email address.</p>
            ) : (
              <div className="app-maint-people">
                {people.map((p) => (
                  <label key={p.id} className="checkbox">
                    <input type="checkbox" checked={to.includes(p.id)} onChange={() => setTo(toggle(to, p.id))} /> {p.name}{' '}
                    <span className="adm-muted">&lt;{p.email}&gt;</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </Modal>
  )
}
